import { useCallback, useEffect, useState } from "react";

import { Link, NavLink, useLocation } from "react-router-dom";
import { clikkleApps } from "../components/SidebarUtils";

//mui component
import {
  AppBar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  keyframes,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Drawer as MuiDrawer,
  Link as MuiLink,
  Skeleton,
  Stack,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material";

//mui icons
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { sharedFile } from "../services/sidebarLinks";
import Image from "../components/Image";
import { useMenu } from "../hooks/useMenu";
import { useTheme } from "../style/theme";

import axios from "axios";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useUser } from "../hooks/Authorize";
import { clearCookie, getCookie, setCookie } from "../utilities/cookies";
import { env, handleAxiosError } from "../utilities/function";
import useModal from "./../hooks/useModal";
import ActionIcon from "./ActionIcon";
import Feedback from "./Feedback";
import { useMessage } from "./Header";
import MicrophoneIcon from "./MicrophoneIcon";
import SearchBar from "./SearchBar";
import { useResourceMenu } from "../context/ResourceMenuContext";
import { useUserProfile } from "../context/UserProfileContext";
import IconLib from "./IconLib";
import ProgressBar from "./ProgressBar";
import StorageUsageWidget from "./StorageUsageWidget";
import StorageFullPopupDialog from "./Dialogs/StorageFullPopupDialog.jsx";

const drawerWidth = 260;
const appsWidth = 54;
const miniDrawerWidth = 60;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  backgroundColor: theme.palette.background.default,
  borderRight: "none",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: theme.palette.background.default,
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 30px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 30px)`,
  },
  borderRight: "none",
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const getInitials = (user) => {
  if (user && (user.firstName || user.lastName)) {
    const firstInitial = user.firstName?.charAt(0).toUpperCase() || "";
    const lastInitial = user.lastName?.charAt(0).toUpperCase() || "";
    return `${firstInitial}${lastInitial}`;
  } else return "?";
};

export default function Navbar(props) {
  const { children } = props;
  const { anchorElAddResource, setAnchorElAddResource } = useResourceMenu();
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarApps, setSidebarApps] = useState(clikkleApps);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [editable, setEditable] = useState(false);
  const [user, setUser] = useState(null);
  const [collapseDrawer, setCollapseDrawer] = useState(false);
  const [drawerHover, setDrawerHover] = useState(false);
  // const {
  //   modalState: feedbackState,
  //   openModal: openFeedback,
  //   closeModal: closeFeedback,
  // } = useModal();

  const [feedbackState, setFeedbackState] = useState(false);

  const openFeedback = () => setFeedbackState(true);
  const closeFeedback = () => setFeedbackState(false);

  const { showError, showResponse } = useMessage();
  const location = useLocation();
  const platformUser = JSON.parse(localStorage.getItem("user"));

  const matches = useMediaQuery("(min-width:1024px)", { noSsr: true });
  const { fsUserProfile, setFsUserProfile } = useUserProfile();
  const [checkSubscriptionUser, setCheckSubscriptionUser] = useState(null);
  const [userTotalSpacePlan, setUserTotalSpacePlan] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  // State variables for StorageFullPopupDialog
  const [isSpaceFull100ModalOpen, setIsSpaceFull100ModalOpen] = useState(false);
  const [isSpaceFull100, setIsSpaceFull100] = useState(false);
  const [is100Full, setIs100Full] = useState(false);

  const { toggleTheme, mode } = useTheme();
  const theme = useMuiTheme();

  const fileManager = [
    {
      name: "Home",
      icon: <img src={IconLib.mdiHomeOutline} alt="" />,
      to: "/",
    },
    {
      name: "My Files",
      icon: <img src={IconLib.icOutlineFolder} alt="" />,
      to: "/my-files",
    },
    {
      name: "Recent",
      icon: <img src={IconLib.iconamoonClockLight} alt="" />,
      to: "/recent",
    },
    {
      name: "Favorite",
      icon: <img src={IconLib.mdiStarOutline} alt="" />,
      to: "/favorite",
    },
    {
      name: "Trash",
      icon: <img src={IconLib.octiconTrash24} alt="" />,
      to: "/trash",
    },
    {
      name: "Shared with me",
      icon: <img src={IconLib.fluentPeople16Regular} alt="" />,
      to: "/shared-with-me",
    },
    {
      name: "Shared by me",
      icon: <img src={IconLib.fluentPerson24Regular} alt="" />,
      to: "/shared-by-me",
    },
  ];

  const getFsUserProfile = useCallback(async () => {
    setIsLoadingProfile(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/user/${platformUser._id}`
      );
      setFsUserProfile(response.data);
      console.log("setFsUserProfile = ", response.data);
    } catch (error) {
      console.error("User Profile Error:", error);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [platformUser._id, setFsUserProfile]);

  const checkSubscriptionDetails = useCallback(async () => {
    setIsLoadingSubscription(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/subscription/${platformUser._id}`
      );

      const data = response.data;
      setCheckSubscriptionUser(data);
      setUserTotalSpacePlan(data?.permissions || "");

      const periodExpiredDate = data?.periods?.[0]?.end;
      const isExpired =
        periodExpiredDate && new Date(periodExpiredDate) < new Date();
      const isActive = data?.isActive;
      const isTypeFile = data?.type === "file";

      if (isExpired || !isActive || !isTypeFile) {
        const redirectUrl = process.env.REACT_APP_STORE_REDIRECT_URL;
        if (redirectUrl) {
          window.location.replace(redirectUrl);
        } else {
          console.error("Redirect URL not defined");
        }
      }
    } catch (error) {
      console.error("Subscription Error:", error?.response?.data);
      setCheckSubscriptionUser({
        success: error?.response?.data?.success || false,
      });
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [platformUser._id]);

  const isLoading = isLoadingProfile || isLoadingSubscription;

  useEffect(() => {
    getFsUserProfile();
    checkSubscriptionDetails();
  }, [getFsUserProfile, checkSubscriptionDetails]);

  // Storage calculation logic - moved higher up for use in other functions
  const BYTES_IN_GB = 1073741824;
  const BYTES_IN_MB = 1048576;

  const noOfAllowed = userTotalSpacePlan?.noOfAllowed ?? 5;
  const format = userTotalSpacePlan?.format ?? "GB";
  const isUnlimited = userTotalSpacePlan?.isUnlimited ?? false;

  let TOTAL_SPACE_GB = 0;
  let percentageUsed = 0;
  let isSpaceFull95 = false;
  let isSpaceFull100Updated = false;

  if (!isUnlimited) {
    if (format === "TB") {
      TOTAL_SPACE_GB = noOfAllowed * 1024;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "GB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedGB = usedBytes / BYTES_IN_GB;
      percentageUsed = (usedGB / TOTAL_SPACE_GB) * 100;
    } else if (format === "MB") {
      TOTAL_SPACE_GB = noOfAllowed;
      const usedBytes = fsUserProfile?.usedSpace ?? 0;
      const usedMB = usedBytes / BYTES_IN_MB;
      percentageUsed = (usedMB / TOTAL_SPACE_GB) * 100;
    }

    isSpaceFull95 = percentageUsed >= 90;
    isSpaceFull100Updated = percentageUsed >= 90;
  }

  const handleOpenMenu = (event) => {
    // Check storage before opening upload menu
    if (isSpaceFull100Updated) {
      setIsSpaceFull100ModalOpen(true);
      return;
    }
    setAnchorElAddResource(event.currentTarget);
  };

  const {
    anchorEl: anchorElProfile,
    openMenu: openProfileMenu,
    closeMenu: closeProfileMenu,
  } = useMenu();

  const {
    anchorEl: anchorElApps,
    openMenu: openAppsMenu,
    closeMenu: closeAppsMenu,
  } = useMenu();

  const {
    anchorEl: anchorElSettings,
    openMenu: openSettingsMenu,
    closeMenu: closeSettingsMenu,
  } = useMenu();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    setIsOrderChanged(true);

    const draggingJob = sidebarApps[source.index];
    sidebarApps.splice(source.index, 1);

    sidebarApps.splice(destination.index, 0, draggingJob);
    setSidebarApps([...sidebarApps]);
  };

  const [showProcessingSnackbar, setShowProcessingSnackbar] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(false);

  const handleProfileUpload = async (event) => {
    if (!event.target.files?.length) return;

    const file = event.target.files[0];
    setProcessingMessage("Uploading profile picture...");
    setShowProcessingSnackbar(true);

    const formData = new FormData();
    formData.append("profilePicture", file);
    formData.append("userId", platformUser._id);
    formData.append("type", "file");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/upload-profile-picture?id=${platformUser._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setFsUserProfile((prevProfile) => ({
        ...prevProfile,
        profilePicture: `${response.data.profilePictureUrl}?${Date.now()}`,
      }));

      console.log("response.data = ", response.data);
    } catch (error) {
      console.error("Profile Upload Failed:", error);
    } finally {
      setShowProcessingSnackbar(false);
      setProcessingMessage("");
      if (event.target) event.target.value = "";
    }
  };

  const getProfile = useCallback(async () => {
    const role = getCookie("role");
    const accessToken = getCookie("accessToken");

    if (!(accessToken && role)) return;

    try {
      const response = await axios.get(
        `http://localhost:4500/${role}/profile`,
        {
          baseURL: env("AUTHENTICATION_SERVER"),
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      const user = response.data.user;
      setUser(user);
    } catch (err) {
      console.log(err);
    }
  }, [setUser]);

  const getPlatforms = useCallback(async () => {
    try {
      const response = await axios.get("/platforms?sortBy=name&direction=1", {
        baseURL: env("AUTHENTICATION_SERVER"),
      });

      const { success, errors, platforms } = response.data;

      if (!success) return showError(errors);

      const SidebarApps = platforms?.filter(
        (platform) => platform.slug !== "e-sign"
      );

      SidebarApps.forEach((app, i) => (app.order = i + 1));

      const arrangedOrder = [];
      user?.personalize?.appsOrder.forEach((order) => {
        SidebarApps.forEach((app, i) => {
          if (order === app.order) {
            arrangedOrder.push(app);
            SidebarApps.splice(i, 1);
          }
        });
      });

      if (arrangedOrder.length)
        setSidebarApps([...arrangedOrder, ...SidebarApps]);
      else setSidebarApps(SidebarApps);
    } catch (e) {
      console.log(e);
    }
  }, [user, showError]);

  const saveOrder = async () => {
    // const accessToken = getCookie("accessToken");
    const appsOrder = sidebarApps.map((app) => app.order);

    try {
      // const response = await axios.patch(
      //   "/user/personalize",
      //   { appsOrder },
      //   {
      //     baseURL: env("AUTHENTICATION_SERVER"),
      //     headers: { Authorization: `Bearer ${accessToken}` },
      //   }
      // );

      // const { success, errors } = response.data;

      // if (!success) return showError(errors);

      setCookie("side_apps_order", appsOrder);
      // showResponse("Setting updated");
    } catch (e) {
      // handleAxiosError(e, showError);
    } finally {
      setIsOrderChanged(false);
    }
  };

  const signOut = () => {
    clearCookie("accessToken");
    localStorage.removeItem("user");
    localStorage.clear();

    const redirectTo =
      env("AUTHENTICATION_CLIENT") +
      "/logout?redirectto=" +
      encodeURIComponent(env("DOMAIN")) +
      "&&referrer=" +
      env("DOMAIN");
    window.location.replace(redirectTo);
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.hash]);

  // useEffect(() => {
  //   getProfile();
  // }, [getProfile]);

  useEffect(() => {
    user && getPlatforms();
  }, [user, getPlatforms]);

  const [sidebarVisible, setSidebarVisible] = useState(
    JSON.parse(localStorage.getItem("sidebarVisible")) ?? false
  );

  useEffect(() => {
    localStorage.setItem("sidebarVisible", JSON.stringify(sidebarVisible));
  }, [sidebarVisible]);

  const drawer = (
    <Box
      minHeight="100dvh"
      color="text.secondary"
      display="flex"
      flexDirection="column"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        component={Link}
        to="/"
        sx={{ textDecoration: "none", color: "text.primary", py: 1 }}
      >
        <Image
          cdn="https://cdn.clikkle.com/images/files/logo/2025/Clikkle%20Files%20Blue%20(%20Black%20Text).png"
          sx={{ height: "35px" }}
        />
        <Typography
          color="text.secondary"
          variant="body2"
          fontWeight="bold"
          sx={{ position: "absolute", bottom: 2, left: "37%" }}
        >
          Beta
        </Typography>
      </Box>

      <Box
        sx={{ overflowY: "auto", height: "calc(100dvh - 90px)", flexGrow: 1 }}
      >
        <Typography
          variant="body2"
          pl={3}
          mt={1.5}
          fontSize="14px"
          fontWeight={500}
        >
          File Manager
        </Typography>
        <List sx={{ px: 3 }}>
          {fileManager.map((link) => (
            <NavLink
              to={link.to}
              key={link.name}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {({ isActive }) => (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isActive}
                    disableRipple
                    disableTouchRipple
                    variant="sidebarButton"
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "35px",
                        color: "text.secondary",
                      }}
                    >
                      {link.icon}
                    </ListItemIcon>
                    <ListItemText primary={link.name} />
                  </ListItemButton>
                </ListItem>
              )}
            </NavLink>
          ))}
        </List>
        <Divider variant="middle" />
        <Typography
          variant="body2"
          pl={3}
          mt={1.5}
          fontSize="14px"
          fontWeight={500}
        >
          Shared File
        </Typography>
        <List sx={{ px: 3 }}>
          {sharedFile.map((link) => (
            <NavLink
              to={link.to}
              key={link.name}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {({ isActive }) => (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isActive}
                    disableRipple
                    disableTouchRipple
                    variant="sidebarButton"
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "35px",
                        color: "text.secondary",
                      }}
                    >
                      {link.icon}
                    </ListItemIcon>
                    <ListItemText primary={link.name} />
                  </ListItemButton>
                </ListItem>
              )}
            </NavLink>
          ))}
        </List>
      </Box>

      <Box>
        <Divider variant="middle" />
        <Typography
          variant="body2"
          pl={3}
          mt={1.5}
          fontSize="14px"
          fontWeight={500}
        >
          Storage
        </Typography>

        <Box px={3} pb={3}>
          <LinearProgress
            variant="determinate"
            value={20}
            color="primary"
            sx={{ borderRadius: "2px", mt: 1 }}
          />
          <Typography
            variant="caption"
            component="div"
            mt={1}
            color="primary.main"
          >
            1 GB used of 5 GB
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudOutlinedIcon fontSize="small" />}
            sx={{ mt: 1, color: "white" }}
            href={env("MY_ACCOUNT")}
            fullWidth
          >
            Upgrade storage
          </Button>
        </Box>
        <Divider
          variant="middle"
          sx={{ display: { xs: "block", sm: "none" } }}
        />
        <List sx={{ px: 1, display: { xs: "block", sm: "none" } }}>
          <ListItem
            disablePadding
            onClick={openSettingsMenu}
            sx={{
              "&:hover": {
                backgroundColor: "custom.cardHover",
                borderRadius: "8px",
              },
            }}
          >
            <ListItemButton
              disableRipple
              disableTouchRipple
              variant="sidebarButton"
            >
              <ListItemIcon
                sx={{
                  minWidth: "30px",
                  color: "text.secondary",
                }}
              >
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Settings"
                primaryTypographyProps={{ fontSize: 14 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Stack
          direction="row"
          justifyContent="center"
          my={1}
          sx={{ display: { xs: "none", sm: "flex" } }}
        >
          <MuiLink
            display="inline-flex"
            alignItems="center"
            color="text.secondary"
            sx={{ cursor: "pointer" }}
            onClick={openFeedback}
          >
            <MicrophoneIcon />
            <Typography variant="caption" fontWeight="bold">
              Give feedback
            </Typography>
          </MuiLink>
        </Stack>
      </Box>
    </Box>
  );

  const miniDrawer = (
    <Box
      minHeight="100dvh"
      color="text.secondary"
      display="flex"
      flexDirection="column"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        component={Link}
        mb={1.5}
        to="/"
        sx={{ textDecoration: "none", color: "text.primary", py: 1 }}
      >
        <Image
          src="https://cdn.clikkle.com/images/files/logo/2025/clikkle%20files.png"
          sx={{ height: "35px" }}
        />
      </Box>

      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          height: "calc(100dvh - 90px)",
          flexGrow: 1,
        }}
      >
        <List
          sx={{
            px: 1,
            py: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {fileManager.map((link) => (
            <NavLink
              to={link.to}
              key={link.name}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              {({ isActive }) => (
                <ListItem
                  disablePadding
                  sx={{
                    mb: link.name.includes("Shared") ? 4 : 2,
                    justifyContent: "center",
                    width: "95%",
                  }}
                >
                  <ListItemButton
                    disableRipple
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "5px",
                      padding: "10px",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      width: "60px",
                      fontSize: "9px",
                      height: "60px",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? "primary.main" : "text.secondary",
                        backgroundColor: isActive
                          ? "rgba(55, 103, 177, 0.08)"
                          : "rgba(255, 255, 255, 0.04)",
                        borderRadius: "5px",
                        width: 40,
                        minWidth: 40,
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {link.icon}
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        link.name.includes("Shared") ? (
                          <Typography>
                            {link.name.split(" ")[0]} <br />{" "}
                            {link.name.split("Shared ")[1]}
                          </Typography>
                        ) : (
                          link.name
                        )
                      }
                      sx={{
                        mt: "6px",
                        textAlign: "center",
                        "& .MuiTypography-root": {
                          fontSize: "9px",
                          color: isActive ? "primary.main" : "text.secondary",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </NavLink>
          ))}
        </List>
      </Box>
    </Box>
  );

  const [isVibrating, setIsVibrating] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);

  const oscillate = keyframes`
    0% { transform: rotate(0deg); }
    10% { transform: rotate(15deg); }
    20% { transform: rotate(20deg); }
    30% { transform: rotate(15deg); }
    40% { transform: rotate(0deg); }
    50% { transform: rotate(-10deg); }
    60% { transform: rotate(-15deg); }
    70% { transform: rotate(-10deg); }
    80% { transform: rotate(0deg); }
    90% { transform: rotate(10deg); }
    100% { transform: rotate(0deg); }
  `;

  const handleBellClick = () => {
    setIsVibrating(true);
    setTimeout(() => setIsVibrating(false), 1000);
  };

  const storeRedirectURL = () => {
    const redirectUrl = process.env.REACT_APP_STORE_REDIRECT_URL;
    if (redirectUrl) {
      window.open(redirectUrl, "_blank");
    } else {
      console.error("Redirect URL not defined");
    }
  };

  console.log(
    isUnlimited,
    noOfAllowed,
    isSpaceFull95,
    isSpaceFull100Updated,
    percentageUsed
  );

  const [isSpaceFull95ModalOpen, setIsSpaceFull95ModalOpen] = useState(false);

  // Updated useEffect to handle both 95% and 100% cases
  useEffect(() => {
    if (isSpaceFull100Updated) {
      setIsSpaceFull100(true);
      setIsSpaceFull100ModalOpen(true);
      setIsSpaceFull95ModalOpen(false);
    } else if (isSpaceFull95) {
      setIsSpaceFull95ModalOpen(true);
      setIsSpaceFull100(false);
      setIsSpaceFull100ModalOpen(false);
    } else {
      setIsSpaceFull95ModalOpen(false);
      setIsSpaceFull100(false);
      setIsSpaceFull100ModalOpen(false);
    }
  }, [isSpaceFull95, isSpaceFull100Updated, setIsSpaceFull100ModalOpen]);

  const handleLearnMore = () => {};

  const handleBuyStorage = () => {
    const redirectUrl = process.env.REACT_APP_STORE_REDIRECT_URL;
    if (redirectUrl) {
      window.open(redirectUrl, "_blank");
    } else {
      console.error("Redirect URL not defined");
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        px: { xs: 0.5, xm: 0 },
        height: "100dvh",
        ml: 3,
        position: "relative",
      }}
    >
      <ProgressBar
        showProcessingSnackbar={showProcessingSnackbar}
        processingMessage={processingMessage}
      />

      <AppBar
        elevation={0}
        component={Box}
        position="sticky"
        sx={{
          width: {
            xs: "100%",
            xm:
              collapseDrawer && !drawerHover
                ? `calc(100% - ${drawerWidth}px)`
                : `calc(100% - ${miniDrawerWidth}px )`,
          },
          ml: {
            xm:
              collapseDrawer && !drawerHover
                ? `${drawerWidth}px`
                : `${miniDrawerWidth}px`,
          },
          backgroundColor: "background.default",
          borderBottom: "1px solid custom.border",
          color: "text.primary",
          transition: "ease-in-out 225ms, background-color 0s",
        }}
      >
        <Toolbar
          sx={{
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            "&": {
              minHeight: "64px",
              px: 1,
            },
          }}
        >
          <Grid container alignItems="center" columnSpacing={1}>
            <Grid
              item
              xs
              md={5}
              paddingLeft={10}
              alignItems="flex-start"
              sx={{
                marginLeft: 0.5,
              }}
            >
              {showSearchBar && <SearchBar />}
            </Grid>
            <Grid item xs display={{ xs: "none", sm: "block" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={0.6}
              >
                {checkSubscriptionUser === null
                  ? null
                  : (!checkSubscriptionUser?.success ||
                      checkSubscriptionUser?.planName === "Free") && (
                      <Button onClick={() => storeRedirectURL()}>
                        <img
                          src={IconLib.upgradeBtn}
                          width={120}
                          alt="Upgrade"
                        />
                      </Button>
                    )}

                <IconButton onClick={handleOpenMenu}>
                  <img src={IconLib.addButton} width={35} alt="upload-file" />
                </IconButton>

                <IconButton
                  onClick={() => {
                    setShowSearchBar(!showSearchBar);
                  }}
                >
                  {mode === "dark" ? (
                    <img src={IconLib.ggSearch} width={25} alt="" />
                  ) : (
                    <img src={IconLib.ggSearchlm} width={25} alt="" />
                  )}
                </IconButton>
                <IconButton
                  onClick={handleBellClick}
                  sx={{
                    animation: isVibrating
                      ? `${oscillate} 1s ease-in-out`
                      : "none",
                  }}
                >
                  {mode === "dark" ? (
                    <img src={IconLib.bytesizeBell} width={25} alt="" />
                  ) : (
                    <img src={IconLib.bytesizeBelllm} width={25} alt="" />
                  )}
                </IconButton>
                <IconButton
                  onClick={() =>
                    window.open("https://chat.clikkle.com", "_blank")
                  }
                >
                  {mode === "dark" ? (
                    <img
                      src={IconLib.iconParkOutlineMessage}
                      width={25}
                      alt=""
                    />
                  ) : (
                    <img
                      src={IconLib.iconParkOutlineMessagelm}
                      width={25}
                      alt=""
                    />
                  )}
                </IconButton>
                <IconButton
                  onClick={() =>
                    window.open("https://crew.clikkle.com", "_blank")
                  }
                >
                  {mode === "dark" ? (
                    <img src={IconLib.basilVideoOutline} width={25} alt="" />
                  ) : (
                    <img src={IconLib.basilVideoOutlinelm} width={25} alt="" />
                  )}
                </IconButton>
                <IconButton
                  onClick={() =>
                    window.open("https://apps.clikkle.com", "_blank")
                  }
                >
                  {mode === "dark" ? (
                    <img src={IconLib.feAppMenu} width={25} alt="" />
                  ) : (
                    <img src={IconLib.feAppMenulm} width={25} alt="" />
                  )}
                </IconButton>
                <IconButton onClick={openSettingsMenu}>
                  {mode === "dark" ? (
                    <img src={IconLib.mdiLightSettings} width={25} alt="" />
                  ) : (
                    <img src={IconLib.mdiLightSettingslm} width={25} alt="" />
                  )}
                </IconButton>
                <Menu
                  anchorEl={anchorElSettings}
                  open={Boolean(anchorElSettings)}
                  onClose={closeSettingsMenu}
                >
                  <MenuItem onClick={toggleTheme}>
                    <ListItemIcon>
                      {mode === "dark" ? (
                        <LightModeIcon fontSize="small" />
                      ) : (
                        <DarkModeIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    Appearance
                  </MenuItem>
                </Menu>
                <Menu
                  anchorEl={anchorElApps}
                  open={Boolean(anchorElApps)}
                  onClose={closeAppsMenu}
                  sx={{
                    ".MuiPaper-root.MuiMenu-paper.MuiPopover-paper": {
                      marginTop: "16px",
                      bgcolor: "custom.menu",
                      width: "300px",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
                      border: "1px solid rgba(0, 0, 0, 0.11)",
                    },
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    {sidebarApps
                      ? sidebarApps.map((app) => (
                          <Grid item xs={4} key={app.name}>
                            <MuiLink
                              href={app.url}
                              target="_blank"
                              sx={{
                                fontWeight: 500,
                                textDecoration: "none",
                                color: "#5f6368",
                              }}
                            >
                              <Box
                                align="center"
                                sx={{
                                  borderRadius: "8px",
                                  p: 1.2,
                                  textAlign: "center",
                                  "&:hover": {
                                    bgcolor: "custom.appsHover",
                                  },
                                }}
                              >
                                <Image
                                  src={app.logo}
                                  sx={{
                                    height: "35px",
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    overflowX: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontWeight: 500,
                                  }}
                                >
                                  {app.name}
                                </Typography>
                              </Box>
                            </MuiLink>
                          </Grid>
                        ))
                      : Array(9)
                          .fill(0)
                          .map((_, i) => (
                            <Grid item xs={4} key={i} align="center">
                              <Skeleton
                                variant="circular"
                                animation="wave"
                                width={37}
                                height={37}
                                sx={{ mt: 1 }}
                              />
                              <Skeleton
                                variant="text"
                                animation="wave"
                                width={38}
                                sx={{ mt: 1 }}
                              />
                            </Grid>
                          ))}
                  </Grid>
                </Menu>
              </Stack>
            </Grid>
            <Grid item>
              <IconButton
                onClick={openProfileMenu}
                sx={{
                  borderWidth: "2px",
                  width: "45px",
                  height: "45px",
                  borderStyle: "solid",
                  borderColor: "primary.main",
                  p: "3px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {fsUserProfile?.profilePicture ? (
                  <Box
                    component="img"
                    src={fsUserProfile.profilePicture}
                    alt="Profile"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  <Typography
                    variant="subtitle1"
                    component="div"
                    fontWeight={600}
                    fontSize="1.0rem"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                  >
                    {getInitials(platformUser)}
                  </Typography>
                )}
              </IconButton>

              <Menu
                anchorEl={anchorElProfile}
                open={Boolean(anchorElProfile)}
                onClose={closeProfileMenu}
                sx={{
                  ".MuiPaper-root.MuiMenu-paper.MuiPopover-paper": {
                    width: "min(100%, 320px)",
                    boxShadow:
                      "rgba(0, 0, 0, 0.1) 0px 20px 25px -5px, rgba(0, 0, 0, 0.04) 0px 10px 10px -5px",
                    border: "1px solid #00000017",
                    bgcolor: "custom.menu",
                    px: 0.5,
                    pt: 1.5,
                  },
                }}
              >
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  flexWrap="nowrap"
                >
                  <Grid
                    item
                    sx={{
                      borderWidth: "2px",
                      width: "85px",
                      marginLeft: "22px",
                      height: "85px",
                      marginTop: "10px",
                      borderRadius: "60px",
                      textAlign: "center",
                      borderStyle: "solid",
                      borderColor: "primary.main",
                      p: "8px",
                      overflow: "hidden",
                      position: "relative",
                      cursor: "pointer",
                      "&:hover .hoverIcon": {
                        opacity: 1,
                      },
                    }}
                  >
                    {fsUserProfile?.profilePicture ? (
                      <Box
                        component="img"
                        src={fsUserProfile.profilePicture}
                        alt="Profile"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    ) : (
                      <Typography
                        variant="subtitle1"
                        component="div"
                        fontWeight={600}
                        sx={{
                          fontSize: "1.5rem",
                          fontFamily: "sans-serif",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: "100%",
                          textAlign: "center",
                          lineHeight: "1",
                        }}
                      >
                        {getInitials(platformUser)}
                      </Typography>
                    )}

                    <input
                      type="file"
                      id="profileUploadInput"
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleProfileUpload(e);
                        }
                      }}
                    />

                    <Box
                      className="hoverIcon"
                      onClick={() =>
                        document.getElementById("profileUploadInput")?.click()
                      }
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        borderRadius: "50%",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 2,
                        width: "40px",
                        height: "40px",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      <CameraAltIcon
                        sx={{ color: "white", fontSize: "20px" }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={8}>
                    <Typography
                      variant="substitle1"
                      component="div"
                      fontWeight={600}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {platformUser.firstName + " " + platformUser.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {platformUser.email}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="a"
                      href={env("MY_ACCOUNT")}
                      color="primary.main"
                      display="block"
                    >
                      My Clikkle account
                    </Typography>
                    <Typography
                      variant="caption"
                      component="a"
                      href="#"
                      color="primary.main"
                      display="block"
                    >
                      My Profile
                    </Typography>
                  </Grid>
                </Grid>

                {fsUserProfile && (
                  <StorageUsageWidget
                    userTotalSpacePlan={userTotalSpacePlan || ""}
                    fsUserProfile={fsUserProfile}
                  />
                )}

                <Stack direction="row" mt={2}>
                  <Button variant="text" fullWidth>
                    Add account
                  </Button>
                  <Button variant="text" onClick={signOut} fullWidth>
                    Sign out
                  </Button>
                </Stack>
                <Stack direction="row">
                  <div className="flex justify-center items-center gap-3 w-full">
                    <p
                      onClick={openFeedback}
                      className="text-[#A5A5A5] text-[12px] cursor-pointer"
                    >
                      Give Feedback
                    </p>
                    <div className="bg-[#A5A5A5] w-[5px] h-[5px] rounded-full" />
                    <Link
                      to={"https://clikkle.com/privacy-policy"}
                      className="text-[#A5A5A5] text-[12px]"
                    >
                      Privacy Policy
                    </Link>
                  </div>
                </Stack>
              </Menu>
            </Grid>
          </Grid>
        </Toolbar>

        {sidebarVisible && (
          <Box
            sx={{
              width: appsWidth,
              display: { xs: "none", xm: "block" },
              backgroundColor: "background.default",
              zIndex: "1200",
              position: "absolute",
              right: 0,
              top: 65,
            }}
          >
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              spacing={1}
              overflow="hidden"
              px={0.8}
            >
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId="apps"
                  isDropDisabled={!editable}
                  key={1}
                >
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {sidebarApps ? (
                        sidebarApps.map((app, i) => (
                          <Draggable
                            key={app.order}
                            draggableId={app.name}
                            index={i}
                            isDragDisabled={!editable}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <ActionIcon
                                  title={editable ? "" : app.name}
                                  href={app.url}
                                  src={app.logo}
                                  key={app.order}
                                  sx={{
                                    mt: 0.8,
                                    width: "35px",
                                  }}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <Box mt={2}>
                          {Array(8)
                            .fill(0)
                            .map((_, i) => (
                              <Skeleton
                                variant="circular"
                                animation="wave"
                                key={i}
                                width={35}
                                height={35}
                                sx={{ mb: 2 }}
                                imageSx={{
                                  filter:
                                    editable &&
                                    `drop-shadow(0px 2px 2px ${theme.palette.primary.main})`,
                                }}
                              />
                            ))}
                        </Box>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <Divider variant="middle" sx={{ my: 2, width: "80%" }} />
              {editable ? (
                <ActionIcon
                  title="Save"
                  icon={<DoneIcon fontSize="small" />}
                  onClick={() => {
                    setEditable(false);
                    if (isOrderChanged) saveOrder();
                  }}
                />
              ) : (
                <ActionIcon
                  title="Edit"
                  icon={<EditIcon fontSize="small" />}
                  onClick={() => setEditable(true)}
                />
              )}
            </Stack>
          </Box>
        )}

        <IconButton
          sx={{
            position: "fixed",
            top: "90%",
            right: 0,
            transform: "translateY(-50%)",
            backgroundColor:
              mode === "dark" ? "#121212" : "rgba(239, 239, 239, 1)",
            color: "white",
            borderRadius: "50% 0 0 50%",
            zIndex: 1300,
            width: 30,
            height: 30,
            minWidth: "auto",
            boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setSidebarVisible(!sidebarVisible)}
        >
          {sidebarVisible ? (
            <>
              {mode === "dark" ? (
                <ArrowForwardIosIcon sx={{ fontSize: "15px" }} />
              ) : (
                <ArrowForwardIosIcon sx={{ fontSize: "15px", color: "#777" }} />
              )}
            </>
          ) : (
            <>
              {mode === "dark" ? (
                <ArrowBackIosNewIcon sx={{ fontSize: "15px" }} />
              ) : (
                <ArrowBackIosNewIcon sx={{ fontSize: "15px", color: "#777" }} />
              )}
            </>
          )}
        </IconButton>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { xm: drawerWidth },
          flexShrink: { sm: 0 },
          bgcolor: "custom.menu",
        }}
      >
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", xm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "custom.menu",
            },
          }}
        >
          {drawer}
        </MuiDrawer>
        <Drawer
          variant="permanent"
          open={collapseDrawer}
          hover={drawerHover}
          onMouseOver={() => {
            return;
          }}
          onMouseLeave={() => {
            return;
          }}
          sx={{
            display: { xs: "none", xm: "block" },
            p: 0,
            "& .MuiDrawer-paper": {
              boxShadow: drawerHover
                ? "rgba(149, 157, 165, 0.2) 0px 8px 24px"
                : "none",
            },
          }}
        >
          {collapseDrawer ? drawer : miniDrawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          width: {
            xs: "100%",
            xm:
              collapseDrawer && !drawerHover
                ? `calc(100% - ${drawerWidth + appsWidth}px)`
                : `calc(100% - ${appsWidth + miniDrawerWidth}px )`,
          },
          ml: {
            xm:
              collapseDrawer && !drawerHover
                ? `${drawerWidth}px`
                : `${miniDrawerWidth}px`,
          },
          mt: 1,
          height: { xs: "calc(100dvh - 90px)" },
          backgroundColor: "background.default",
          borderRadius: "12px",
        }}
      >
        {children}
      </Box>

      <Modal
        open={feedbackState}
        onClose={closeFeedback}
        sx={{
          overflowY: "scroll",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Feedback open={feedbackState} onClose={closeFeedback} />
      </Modal>

      {/* StorageFullPopupDialog Component - Only show if actually 100% full */}
      {isSpaceFull100Updated && (
        <StorageFullPopupDialog
          isSpaceFull100ModalOpen={isSpaceFull100ModalOpen}
          setIsSpaceFull100ModalOpen={setIsSpaceFull100ModalOpen}
          isSpaceFull100={isSpaceFull100Updated}
          is100Full={isSpaceFull100Updated}
          isUnlimited={isUnlimited}
          noOfAllowed={noOfAllowed}
          format={format}
          dialogLoading={isLoading}
        />
      )}

      {/* Existing 95% full modal */}
      {!isLoading && isSpaceFull95ModalOpen && isSpaceFull95 && (
        <Modal
          open={isSpaceFull95ModalOpen && isSpaceFull95}
          onClose={() => setIsSpaceFull95ModalOpen(false)}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              bgcolor: mode === "dark" ? "#121212" : "#FCE8E7",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <WarningAmberRoundedIcon
                color="warning"
                sx={{ fontSize: 50, mt: 0.5 }}
              />

              <Box flex={1}>
                <Typography id="modal-modal-description" sx={{ mt: 1 }}>
                  {isUnlimited ? (
                    "You have unlimited storage. If you're seeing this, please contact your administrator."
                  ) : (
                    <>
                      Your storage is almost full. You've used{" "}
                      {percentageUsed.toFixed(1)}% of your {noOfAllowed}
                      {format} individual storage. Consider freeing up space or
                      upgrading your plan.
                    </>
                  )}
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="flex-end"
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="text"
                    sx={{
                      color: "#D32F2F",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#fddddd",
                      },
                    }}
                    onClick={handleLearnMore}
                  >
                    Learn More
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#D32F2F",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#b71c1c",
                      },
                    }}
                    onClick={handleBuyStorage}
                  >
                    Buy Storage
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Modal>
      )}
    </Box>
  );
}
